import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CheckboxInputStyle,
  UncontrolledRadioInput,
  UncontrolledTextInput,
} from '@src/components/Inputs'
import { SectionHeader } from '@src/components/ServicePopover/SectionHeader'
import { BottomSheet } from '@src/components/BottomSheet/BottomSheet'
import {
  MobileNavigationPopoverContentWrapper,
  MobileNavigationPopoverContent,
  MobileNavigationPopoverFooter,
  MobileNavigationPopoverFooterContent,
  MobileNavigationResetLink,
  DoneButton,
  PopoverOptionContainer,
} from '@src/components/BottomSheet/BottomSheet.styles'
import { useLazyQuery } from '@apollo/client'
import { useMarketplace } from '../../hooks/useMarketplace'
import { SearchAddressDocument } from './queries/__generated__/SearchAddress.graphql-interface'
import { debounce } from 'lodash'
import { PlaceSearchLocation } from '../../hooks/useFulfilmentFilter/validation'
import { LoadingSpinner } from '../Loaders/LoadingSpinner'
import { useBottomSheetStack } from '../../hooks/useBottomSheetStack/useBottomSheetStack'

type PlaceSearchLocationWithoutType = Omit<PlaceSearchLocation, 'type'>

export const PlaceSearchPopover: React.FC<{
  onChange?: (place: PlaceSearchLocationWithoutType) => void
  onDone: (placeId: PlaceSearchLocationWithoutType) => void
  isPopover: boolean
}> = ({ onChange, onDone, isPopover }) => {
  const { placeSearchBottomSheetController } = useBottomSheetStack()
  const { t } = useTranslation('serviceNavigation')
  const [searchTerm, setSearchTerm] = useState('')
  const { id } = useMarketplace()
  // not tied to query - triggered by user input
  const [loading, setLoading] = useState(false)
  const [newPlace, setNewPlace] =
    useState<PlaceSearchLocationWithoutType | null>(null)

  const [triggerQuery, { data: _data, previousData }] = useLazyQuery(
    SearchAddressDocument,
    { onCompleted: () => setLoading(false) }
  )

  const data = _data || previousData

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        if (term.length >= 5) {
          void triggerQuery({
            variables: {
              searchTerm: term,
              marketplaceId: id,
            },
          })
        }
      }, 500),
    [id, triggerQuery]
  )

  useEffect(() => {
    if (searchTerm.length >= 5) {
      setLoading(true)
      debouncedSearch(searchTerm)
    }
  }, [searchTerm, debouncedSearch])

  return (
    <BottomSheet
      bottomSheetController={placeSearchBottomSheetController}
      maxWidth={584}
      showBackdrop={!isPopover}
    >
      <MobileNavigationPopoverContentWrapper>
        <MobileNavigationPopoverContent>
          <SectionHeader
            header={t('location_header')}
            subheader={t('place_search_option_subtitle')}
          />
          <br />
          <UncontrolledTextInput
            label={t('search_label')}
            name={'search-place'}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder={t('search_placeholder')}
            value={searchTerm}
            touched={false}
            required={false}
          />
          {loading ? (
            <LoadingSpinner />
          ) : (
            data?.searchAddress?.map?.(place => (
              <PopoverOptionContainer key={place.placeId}>
                <UncontrolledRadioInput
                  label={place.address}
                  value={place.placeId}
                  checked={newPlace?.placeId === place.placeId}
                  style={CheckboxInputStyle.TICK}
                  onChange={() => {
                    setNewPlace({
                      placeId: place.placeId,
                      address: place.address,
                    })
                    onChange?.({
                      placeId: place.placeId,
                      address: place.address,
                    })
                  }}
                />
              </PopoverOptionContainer>
            ))
          )}
        </MobileNavigationPopoverContent>
      </MobileNavigationPopoverContentWrapper>
      <MobileNavigationPopoverFooter>
        <MobileNavigationPopoverFooterContent>
          <MobileNavigationResetLink
            onClick={placeSearchBottomSheetController.close}
          >
            {t('cancel')}
          </MobileNavigationResetLink>

          <DoneButton
            content={t('done')}
            width="180px"
            onClick={e => {
              e?.stopPropagation()
              newPlace && onDone(newPlace)
            }}
            type={'button'}
            disabled={!newPlace}
          />
        </MobileNavigationPopoverFooterContent>
      </MobileNavigationPopoverFooter>
    </BottomSheet>
  )
}
